// Imports
// ********************************************
import { useCallback } from 'react';
import { useSession } from 'next-auth/react';
import { useSWRConfig } from 'swr';
import { useAppSelector } from 'src/app';
import { getUserHeaderInfo } from 'src/app/reducers/userSelector';

const HEADER_INFO_URL = '/v1/header-info';

/**
 * @description Read the current user's header info from Redux. **No network, no SWR subscription,
 * no event listeners.**
 *
 * Use this anywhere you only need to *read* header info. The single fetching owner is
 * `useHeaderInfoData` in the Header (`@components/layout/Header/Header`), which fills this Redux
 * slice via its `onSuccess`.
 *
 * Why this exists: `useHeaderInfoData` also returned its data straight from Redux — SWR's own
 * `data` was never exposed — so every consumer that just wanted the value was paying for a full SWR
 * subscription plus a `visibilitychange` listener to get it. On a job list that meant 24+ copies,
 * and one tab refocus fired 24 concurrent `/v1/header-info` requests (bound `mutate()` bypasses
 * SWR deduping on purpose).
 *
 * @example
 * const headerInfo = useHeaderInfo();
 * const isCompany = headerInfo?.hasBusinessWallet;
 */
export const useHeaderInfo = () => useAppSelector(getUserHeaderInfo);


/**
 * @description Trigger a refresh of the header info **without subscribing to it**.
 *
 * Uses SWR's global mutate, which revalidates whichever mounted hook holds the `/v1/header-info`
 * key — the Header. Callers therefore get a refresh without their own subscription or refocus
 * listener. Same filter semantics as `useSwrGlobal().revalidateKey`, but built on the stable
 * `mutate` from `useSWRConfig` so the returned callbacks keep a stable identity (they are consumed
 * in `useEffect` dependency arrays — see `after-apply/page-content.tsx`).
 *
 * - **refreshHeaderInfo** — revalidate now. For imperative "I changed something, re-read it" cases.
 * - **forceCheckAuthStatus** — same, but when the NextAuth session is *not* authenticated it first
 *   refetches the session, then revalidates. The after-apply flow depends on this branch: the
 *   session can still be stale right after an apply-triggered sign-in.
 *
 * @example
 * const { refreshHeaderInfo, forceCheckAuthStatus } = useHeaderInfoRefresh();
 */
export const useHeaderInfoRefresh = () => {
  const { data: session, status, update: updateSession } = useSession();
  const { mutate } = useSWRConfig();
  const isAuthenticated = status === 'authenticated' && !!session;

  const refreshHeaderInfo = useCallback(() => {
    void mutate((key) => Array.isArray(key) && typeof key[0] === 'string' && key[0].includes(HEADER_INFO_URL));
  }, [mutate]);

  const forceCheckAuthStatus = useCallback(async () => {
    if (!isAuthenticated) {
      try {
        await updateSession();
      } catch {
        // Ignore errors — still attempt the revalidation below.
      }
    }

    refreshHeaderInfo();
  }, [isAuthenticated, updateSession, refreshHeaderInfo]);

  return {
    refreshHeaderInfo,
    forceCheckAuthStatus,
    isSessionAuthenticated: isAuthenticated,
    isSessionLoading: status === 'loading',
  };
};
